import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { DashboardLayout } from "@/components/layout/DashboardLayout";

import Home from "@/pages/public/Home";
import HowItWorks from "@/pages/public/HowItWorks";
import ForMSMEs from "@/pages/public/ForMSMEs";
import ForBanks from "@/pages/public/ForBanks";
import PublicSchemes from "@/pages/public/PublicSchemes";
import CGTMSEInfo from "@/pages/public/CGTMSEInfo";
import About from "@/pages/public/About";
import Contact from "@/pages/public/Contact";
import LegalPage from "@/pages/public/LegalPage";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import DemoTour from "@/pages/public/DemoTour";
import { TourOverlay } from "@/components/tour/TourOverlay";

import BorrowerDashboard from "@/pages/borrower/Dashboard";
import Passport from "@/pages/borrower/Passport";
import Readiness from "@/pages/borrower/Readiness";
import FundingRequirements from "@/pages/borrower/FundingRequirements";
import FinancingRoutes from "@/pages/borrower/FinancingRoutes";
import SchemeMatches from "@/pages/borrower/SchemeMatches";
import LenderInterest from "@/pages/borrower/LenderInterest";
import OfferComparison from "@/pages/borrower/OfferComparison";
import ConsentCentre from "@/pages/borrower/ConsentCentre";
import Applications from "@/pages/borrower/Applications";
import ApplicationDetail from "@/pages/borrower/ApplicationDetail";
import Documents from "@/pages/borrower/Documents";
import SubsidyTracker from "@/pages/borrower/SubsidyTracker";
import Grievances from "@/pages/borrower/Grievances";

import BankDashboard from "@/pages/bank/Dashboard";
import OpportunityMarketplace from "@/pages/bank/OpportunityMarketplace";
import OpportunityDetail from "@/pages/bank/OpportunityDetail";
import BankApplications from "@/pages/bank/BankApplications";
import BankApplicationDetail from "@/pages/bank/BankApplicationDetail";
import Policies from "@/pages/bank/Policies";
import Analytics from "@/pages/bank/Analytics";

import AdminDashboard from "@/pages/admin/Dashboard";
import AdminUsers from "@/pages/admin/Users";
import AdminSchemes from "@/pages/admin/Schemes";
import AdminLenders from "@/pages/admin/Lenders";
import AdminGrievances from "@/pages/admin/Grievances";
import AdminAudit from "@/pages/admin/AuditLogs";

import GovDashboard from "@/pages/government/Dashboard";
import GovDistrictMap from "@/pages/government/DistrictMap";
import GovSummaryPrint from "@/pages/government/SummaryPrint";
import GovSector from "@/pages/government/SectorAnalysis";
import GovSchemes from "@/pages/government/SchemeUtilization";
import GovInclusion from "@/pages/government/Inclusion";

import FacilitatorDashboard from "@/pages/facilitator/Dashboard";
import Settings from "@/pages/common/Settings";

function DashboardRouter() {
  const { user } = useAuth();
  if (!user) return null;
  const r = user.role;
  if (r === "borrower" || r === "facilitator") return <BorrowerDashboard />;
  if (r === "bank_admin" || r === "bank_rm") return <BankDashboard />;
  if (r === "platform_admin") return <AdminDashboard />;
  if (r === "government") return <GovDashboard />;
  return <BorrowerDashboard />;
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <TourOverlay />
          <Routes>
            {/* Public */}
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/how-it-works" element={<PublicLayout><HowItWorks /></PublicLayout>} />
            <Route path="/for-msmes" element={<PublicLayout><ForMSMEs /></PublicLayout>} />
            <Route path="/for-banks" element={<PublicLayout><ForBanks /></PublicLayout>} />
            <Route path="/schemes" element={<PublicLayout><PublicSchemes /></PublicLayout>} />
            <Route path="/cgtmse" element={<PublicLayout><CGTMSEInfo /></PublicLayout>} />
            <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
            <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
            <Route path="/privacy" element={<PublicLayout><LegalPage title="Privacy Policy" /></PublicLayout>} />
            <Route path="/terms" element={<PublicLayout><LegalPage title="Terms of Use" /></PublicLayout>} />
            <Route path="/disclaimer" element={<PublicLayout><LegalPage title="Disclaimer" /></PublicLayout>} />
            <Route path="/grievance" element={<PublicLayout><LegalPage title="Grievance Redressal" /></PublicLayout>} />
            <Route path="/demo" element={<PublicLayout><DemoTour /></PublicLayout>} />
            <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
            <Route path="/register" element={<PublicLayout><Register /></PublicLayout>} />

            {/* Dashboard router */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout><DashboardRouter /></DashboardLayout></ProtectedRoute>} />

            {/* Borrower */}
            <Route path="/borrower/passport" element={<ProtectedRoute roles={["borrower", "facilitator"]}><DashboardLayout><Passport /></DashboardLayout></ProtectedRoute>} />
            <Route path="/borrower/readiness" element={<ProtectedRoute roles={["borrower", "facilitator"]}><DashboardLayout><Readiness /></DashboardLayout></ProtectedRoute>} />
            <Route path="/borrower/funding" element={<ProtectedRoute roles={["borrower", "facilitator"]}><DashboardLayout><FundingRequirements /></DashboardLayout></ProtectedRoute>} />
            <Route path="/borrower/routes" element={<ProtectedRoute roles={["borrower", "facilitator"]}><DashboardLayout><FinancingRoutes /></DashboardLayout></ProtectedRoute>} />
            <Route path="/borrower/schemes" element={<ProtectedRoute roles={["borrower", "facilitator"]}><DashboardLayout><SchemeMatches /></DashboardLayout></ProtectedRoute>} />
            <Route path="/borrower/interests" element={<ProtectedRoute roles={["borrower", "facilitator"]}><DashboardLayout><LenderInterest /></DashboardLayout></ProtectedRoute>} />
            <Route path="/borrower/compare" element={<ProtectedRoute roles={["borrower", "facilitator"]}><DashboardLayout><OfferComparison /></DashboardLayout></ProtectedRoute>} />
            <Route path="/borrower/consent" element={<ProtectedRoute roles={["borrower", "facilitator"]}><DashboardLayout><ConsentCentre /></DashboardLayout></ProtectedRoute>} />
            <Route path="/borrower/applications" element={<ProtectedRoute roles={["borrower", "facilitator"]}><DashboardLayout><Applications /></DashboardLayout></ProtectedRoute>} />
            <Route path="/borrower/applications/:id" element={<ProtectedRoute roles={["borrower", "facilitator"]}><DashboardLayout><ApplicationDetail /></DashboardLayout></ProtectedRoute>} />
            <Route path="/borrower/documents" element={<ProtectedRoute roles={["borrower", "facilitator"]}><DashboardLayout><Documents /></DashboardLayout></ProtectedRoute>} />
            <Route path="/borrower/subsidy" element={<ProtectedRoute roles={["borrower", "facilitator"]}><DashboardLayout><SubsidyTracker /></DashboardLayout></ProtectedRoute>} />
            <Route path="/borrower/grievances" element={<ProtectedRoute><DashboardLayout><Grievances /></DashboardLayout></ProtectedRoute>} />

            {/* Bank */}
            <Route path="/bank/opportunities" element={<ProtectedRoute roles={["bank_admin", "bank_rm"]}><DashboardLayout><OpportunityMarketplace /></DashboardLayout></ProtectedRoute>} />
            <Route path="/bank/opportunities/:id" element={<ProtectedRoute roles={["bank_admin", "bank_rm"]}><DashboardLayout><OpportunityDetail /></DashboardLayout></ProtectedRoute>} />
            <Route path="/bank/applications" element={<ProtectedRoute roles={["bank_admin", "bank_rm"]}><DashboardLayout><BankApplications /></DashboardLayout></ProtectedRoute>} />
            <Route path="/bank/applications/:id" element={<ProtectedRoute roles={["bank_admin", "bank_rm"]}><DashboardLayout><BankApplicationDetail /></DashboardLayout></ProtectedRoute>} />
            <Route path="/bank/policies" element={<ProtectedRoute roles={["bank_admin", "bank_rm"]}><DashboardLayout><Policies /></DashboardLayout></ProtectedRoute>} />
            <Route path="/bank/analytics" element={<ProtectedRoute roles={["bank_admin", "bank_rm"]}><DashboardLayout><Analytics /></DashboardLayout></ProtectedRoute>} />

            {/* Admin */}
            <Route path="/admin/users" element={<ProtectedRoute roles={["platform_admin"]}><DashboardLayout><AdminUsers /></DashboardLayout></ProtectedRoute>} />
            <Route path="/admin/schemes" element={<ProtectedRoute roles={["platform_admin"]}><DashboardLayout><AdminSchemes /></DashboardLayout></ProtectedRoute>} />
            <Route path="/admin/lenders" element={<ProtectedRoute roles={["platform_admin"]}><DashboardLayout><AdminLenders /></DashboardLayout></ProtectedRoute>} />
            <Route path="/admin/grievances" element={<ProtectedRoute roles={["platform_admin"]}><DashboardLayout><AdminGrievances /></DashboardLayout></ProtectedRoute>} />
            <Route path="/admin/audit" element={<ProtectedRoute roles={["platform_admin"]}><DashboardLayout><AdminAudit /></DashboardLayout></ProtectedRoute>} />

            {/* Government */}
            <Route path="/gov/district-map" element={<ProtectedRoute roles={["government"]}><DashboardLayout><GovDistrictMap /></DashboardLayout></ProtectedRoute>} />
            <Route path="/gov/summary" element={<ProtectedRoute roles={["government", "platform_admin"]}><GovSummaryPrint /></ProtectedRoute>} />
            <Route path="/gov/sector" element={<ProtectedRoute roles={["government"]}><DashboardLayout><GovSector /></DashboardLayout></ProtectedRoute>} />
            <Route path="/gov/schemes" element={<ProtectedRoute roles={["government"]}><DashboardLayout><GovSchemes /></DashboardLayout></ProtectedRoute>} />
            <Route path="/gov/inclusion" element={<ProtectedRoute roles={["government"]}><DashboardLayout><GovInclusion /></DashboardLayout></ProtectedRoute>} />

            {/* Facilitator */}
            <Route path="/facilitator/borrowers" element={<ProtectedRoute roles={["facilitator"]}><DashboardLayout><FacilitatorDashboard /></DashboardLayout></ProtectedRoute>} />

            {/* Shared settings (any signed-in role) */}
            <Route path="/settings" element={<ProtectedRoute><DashboardLayout><Settings /></DashboardLayout></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
